<template>
  <app-panel class="diagnostics-filter">
    <label slot="panel-header">
      {{ $t('fields.general.filters') }}
    </label>
    <div slot="panel-body">
      <b-container fluid>
        <b-row class="mb-3 mt-2">
          <b-col
            xs="12"
            md="6"
          >
            <label>
              {{ $t('diagnostics-configurations.module') }}
            </label>
            <s-select
              v-model="filter.module"
              value-field="id"
              text-field="name"
              :options="modulesOptions"
            />
          </b-col>
          <b-col
            xs="12"
            md="6"
          >
            <label>
              {{ $t('diagnostics-configurations.norm') }}
            </label>
            <s-select
              v-model="filter.norm"
              value-field="id"
              text-field="name"
              :disabled="isNormDisabled"
              :options="norms"
            />
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col
            xs="12"
            md="6"
          >
            <label>
              {{ $t('diagnostics-configurations.classification') }}
            </label>
            <s-enum-select
              v-model="filter.classification"
              :enum-definition="classifications"
              selectOptionTranslation="flags-dropdown.all-female"
              translation-path="enums.engineering.diagnostics-history"
            />
          </b-col>
          <b-col
            xs="12"
            md="6"
          >
            <label>
              {{ $t('diagnostics-configurations.generates-alarm') }}
            </label>
            <s-select
              v-model="filter.generatesAlarm"
              :options="generatesAlarmOptions"
            />
          </b-col>
        </b-row>
        <b-checkbox
          class="mb-3"
          v-model="showAdvancedFilters"
        >
          <span class="font-weight-normal">
            {{ $t('diagnostics-configurations.advanced-filters') }}
          </span>
        </b-checkbox>
        <div
          class="diagnostics-filter__advanced-filters"
          :class="{ 'diagnostics-filter__advanced-filters_collapsed': !showAdvancedFilters }"
        >
          <diagnostics-list
            class="mb-3"
            :chromatography="chromatographyDiagnostics"
            :physical-chemical="physicalChemicalDiagnostics"
            :oscillography-analysis="oscillographyAnalysisDiagnostics"
            :selected-chromatography-diagnostics.sync="selectedChromatographyDiagnostics"
            :selected-physical-chemical-diagnostics.sync="selectedPhysicalChemicalDiagnostics"
            :selected-oscillography-analysis-diagnostics.sync="
              selectedOscillographyAnalysisDiagnostics"
          />
          <reports-filter
            class="mb-5"
            dropdown-direction="below"
            :filter.sync="filter.targetAssets"
          />
        </div>
        <b-row
          class="diagnostics-configurations__form-row"
          align-h="end"
        >
          <b-col
            class="p-md-0 mb-3"
            sm="12"
            md="auto"
          >
            <b-button
              class="diagnostics-configurations__filters-buttons"
              block
              @click="cleanAllFilters"
            > {{ $t('general.clean-filters') }}
            </b-button>
          </b-col>
          <b-col
            class="pl-md-2 mb-3"
            sm="12"
            md="auto"
          >
            <b-button
              class="diagnostics-configurations__filters-buttons"
              block
              variant="primary"
              @click="search"
            > {{ $t('general.search') }}
            </b-button>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </app-panel>
</template>

<script>
import {
  normsMap,
  rulesMap,
  ClassificationType,
  Modules,
} from 'sigma-mappings';
import diagnosticsService from '@/domain/services/generalPanel/reports/diagnostics';
import diagnosticsConfigurationsService from '@/domain/services/generalPanel/adminPanel/diagnosticsConfigurations/diagnosticsConfigurations';
import { events } from '@/domain/services/generalPanel/reports/localEventBus';
import DiagnosticsList from '@/domain/components/generalPanel/reports/diagnosticsHistory/DiagnosticList';
import ReportsFilter from '@/components/ReportsFilter';
import globalEventBus from '@/services/globalEventBus';

export default {
  name: 'DiagnosticsFilter',
  components: {
    DiagnosticsList,
    ReportsFilter,
  },
  data: vm => ({
    selectedChromatographyDiagnostics: [],
    selectedPhysicalChemicalDiagnostics: [],
    selectedOscillographyAnalysisDiagnostics: [],
    modules: [],
    diagnosticsFilter: [],
    classifications: ClassificationType,
    showAdvancedFilters: false,
    generatesAlarmOptions: [
      {
        text: vm.$t('flags-dropdown.all-female'),
        value: null,
      },
      {
        text: vm.$t('general.yes'),
        value: true,
      },
      {
        text: vm.$t('general.no'),
        value: false,
      },
    ],
    filter: {
      module: null,
      norm: null,
      classification: null,
      generatesAlarm: null,
      diagnosticsIds: [],
      targetAssets: {},
    },
  }),
  computed: {
    chromatographyDiagnostics() {
      return this.diagnosticsFilter.find(
        diagnosis => diagnosis.module === Modules.CHROMATOGRAPHY,
      );
    },
    physicalChemicalDiagnostics() {
      return this.diagnosticsFilter.find(
        diagnosis => diagnosis.module === Modules.PHYSICAL_CHEMICAL,
      );
    },
    oscillographyAnalysisDiagnostics() {
      return this.diagnosticsFilter.find(
        diagnosis => diagnosis.module === Modules.OSCILLOGRAPHY_ANALYSIS,
      );
    },
    normsEnums() {
      return {
        'physical-chemical': Object.values(normsMap),
        chromatography: Object.values(rulesMap),
      };
    },
    norms() {
      const norms = this.getNormByModules();
      return [
        {
          id: null,
          name: this.$t('flags-dropdown.all-female'),
        },
        ...norms,
      ];
    },
    modulesOptions() {
      return [
        {
          id: null,
          name: this.$t('flags-dropdown.all'),
        },
        {
           id: Modules.CHROMATOGRAPHY,
          name: this.$t('diagnostics-configurations.modules.chromatography'),
        },
        {
          id: Modules.PHYSICAL_CHEMICAL,
          name: this.$t('diagnostics-configurations.modules.physical-chemical'),
        },
        {
          id: Modules.OSCILLOGRAPHY_ANALYSIS,
          name: this.$t('diagnostics-configurations.modules.oscillography-analysis'),
        },
      ];
    },
    isNormDisabled() {
      return this.filter.module === Modules.OSCILLOGRAPHY_ANALYSIS;
    },
  },
  created() {
    this.getDiagnostics();
  },
  methods: {
    clearDiagnosticsList() {
      this.selectedChromatographyDiagnostics = [];
      this.selectedPhysicalChemicalDiagnostics = [];
      this.selectedOscillographyAnalysisDiagnostics = [];
    },
    cleanAllFilters() {
      this.filter = {
        module: null,
        norm: null,
        classification: null,
        generatesAlarm: null,
        diagnosticsIds: [],
        targetAssets: {},
      };
      this.clearDiagnosticsList();
      this.clearReportsFilter();
      this.getDiagnostics();
    },
    clearReportsFilter() {
      globalEventBus.emit(events.CLEAR_REPORTS_FILTER);
    },
    nullSetter(value) {
      if (value === null) {
        return value;
      }
      return [value];
    },
    getDiagnostics() {
      const chromaModules = [Modules.PHYSICAL_CHEMICAL, Modules.CHROMATOGRAPHY];
      const selectedModule = this.filter.module;

      const wantedChromaModules = selectedModule
        ? chromaModules.filter(module => module === selectedModule)
        : chromaModules;

      const chromaRequest = wantedChromaModules.length ? diagnosticsService.getDiagnostics({
        modules: wantedChromaModules,
        norms: this.nullSetter(this.filter.norm),
        classifications: this.nullSetter(this.filter.classification),
        generatesAlarm: this.filter.generatesAlarm,
      }) : Promise.resolve([]);

      const wantsOscillographyAnalysis = !selectedModule
        || selectedModule === Modules.OSCILLOGRAPHY_ANALYSIS;

      const oscillographyAnalysisRequest = wantsOscillographyAnalysis
        ? diagnosticsConfigurationsService.getOscillographyAnalysisClassifications({
          classification: this.filter.classification,
          generatesAlarm: this.filter.generatesAlarm,
        }).then(response => ({
          module: Modules.OSCILLOGRAPHY_ANALYSIS,
          norm: response.norms,
        })) : Promise.resolve(null);

      return Promise.allSettled([chromaRequest, oscillographyAnalysisRequest])
        .then(([chroma, oscillographyAnalysis]) => {
          const chromaDiagnostics = chroma.status === 'fulfilled' ? chroma.value : [];
          const oscillographyDiagnostics = oscillographyAnalysis.status === 'fulfilled'
            && oscillographyAnalysis.value
            ? [oscillographyAnalysis.value]
            : [];

          this.diagnosticsFilter = [...chromaDiagnostics, ...oscillographyDiagnostics];
        });
    },
    getNormByModules() {
      if (!this.filter.module) {
        return this.getAllNorms();
      }
      const norms = this.normsEnums[this.filter.module] || [];
      return norms.map(item => ({
        id: item,
        name: this.$t(`diagnostics-configurations.modules.norms.${item}`),
      }));
    },
    getAllNorms() {
      const norms = [];
      this.modules.forEach((module) => {
        const moduleNorms = this.normsEnums[module] || [];
        moduleNorms.forEach((normEnum) => {
          norms.push({
            id: normEnum,
            name: this.$t(`diagnostics-configurations.modules.norms.${normEnum}`),
          });
        });
      });
      return norms;
    },
    search() {
      this.filter.diagnosticsIds = [
        ...this.selectedChromatographyDiagnostics,
        ...this.selectedPhysicalChemicalDiagnostics,
        ...this.selectedOscillographyAnalysisDiagnostics,
      ];
      if (!this.showAdvancedFilters) {
        this.filter.targetAssets = {};
        this.filter.diagnosticsIds = [];
      }
      this.$emit('search', this.filter);
    },
  },
};
</script>

<style lang="sass" scoped>
  .diagnostics-filter
    &__advanced-filters
      max-height: 900px
      overflow-y: visible
      overflow-x: hidden
      transition: max-height 0.2s ease-in-out
      animation: 0.3s hide-scroll backwards
      @keyframes hide-scroll
        from, to
          overflow: hidden
    &__advanced-filters_collapsed
      max-height: 0
      overflow: hidden
      transition: max-height 0.2s ease-in-out
    &__buttons
      display: flex
      justify-content: flex-end
      margin: 10px
</style>

<style lang="sass">
  .diagnostics-filter
    .app-panel__body
      overflow: visible !important
</style>
